import { useEffect, useState, useRef, useMemo } from 'react';
import { IDriverUser } from '../../types/type';
import Spiner from '../Spiner/Spiner';
import './driverTechCheckList.css';
import { TechCheckService, TechCheckFilters } from '../../services/techcheck.service';

import { fullName, normalizeImageUrl } from '../../utils/utils';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import Pagination from '../Pagination/Pagination';
import SimpleManyPhotoContainer from '../SimpleManyPhotoContainer/SimpleManyPhotoContainer';
import DateRangeFilter from '../DateRangeFilter/DateRangeFilter';
import { useFilters, PaginatedFilters } from '../../hooks/useFilters';
import Table, { ColumnConfig, ExpandedRowConfig, LabeledCell, SecondaryRowConfig } from '../Table/Table';
import StatusCell from '../Table/StatusCell';

interface IProps {
  driver: IDriverUser;
}

// Расширяем интерфейс TechCheckFilters, чтобы он соответствовал PaginatedFilters
interface ExtendedTechCheckFilters extends PaginatedFilters {
  // Дополнительные поля, если они есть в TechCheckFilters
}

// Интерфейс для элемента технического осмотра
interface ITechCheck {
  id: string;
  num: string | number; // num может быть как строкой, так и числом
  checkTime: string;
  technicFirstName: string;
  technicLastName: string;
  technicMiddleName?: string | null;
  checkStatus: string;
  odometerReading: number;
  engine: boolean;
  optic: boolean;
  body: boolean;
  chassis: boolean;
  odometerPhoto?: string;
  carImages?: Array<{
    photoType: string;
    photoUrl: string;
  }>;
  reasonForRefusal?: string;
}

// Интерфейс для изображения автомобиля
interface ICarImage {
  photoType: string;
  photoUrl: string;
}

export default function DriverTechCheckList({ driver }: IProps) {
  const [techChecks, setTechChecks] = useState<ITechCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { filters, handleFilterChange, resetFilters, handlePageChange } = useFilters<ExtendedTechCheckFilters>({
    page: 1,
    limit: 10,
    startTime: '',
    endTime: '',
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Статус-маппинг для визуализации статусов
  const statusMap = {
    completed: { label: 'Допущен', dotClass: 'active' },
    rejected: { label: 'Не допущен', dotClass: '' },
    waiting: { label: 'Ожидает', dotClass: 'waiting' },
  };

  // Конфигурация колонок для таблицы
  const tableColumns: ColumnConfig[] = [
    {
      id: 'date',
      title: 'Дата',
      render: (row: ITechCheck) => (
        <>
          <div className="table-cell-title">Дата</div>
          <div className="table-cell-value">{formatBackendShortDate(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'num',
      title: 'Номер заявки',
      render: (row: ITechCheck) => (
        <>
          <div className="table-cell-title">Номер заявки</div>
          <div className="table-cell-value">{row.num}</div>
        </>
      ),
    },
    {
      id: 'time',
      title: 'Время',
      render: (row: ITechCheck) => (
        <>
          <div className="table-cell-title">Время</div>
          <div className="table-cell-value">{formatBackendTime(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'odometer',
      title: 'Пробег',
      render: (row: ITechCheck) => (
        <>
          <div className="table-cell-title">Пробег</div>
          <div className="table-cell-value">{`${row.odometerReading} км`}</div>
        </>
      ),
    },

    {
      id: 'status',
      title: 'Статус',
      render: (row: ITechCheck) => {
        const status =
          row.checkStatus === 'completed' ? 'completed' : row.checkStatus === 'rejected' ? 'rejected' : 'waiting';
        return <StatusCell status={status} statusMap={statusMap} />;
      },
      className: 'table-cell-centered',
    },
    {
      id: 'actions',
      title: 'Смотреть',
      render: () => null, // Рендеринг происходит внутри компонента Table
    },
  ];

  // Дополнительная базовая информация (видна без раскрытия)
  const secondaryRows: SecondaryRowConfig[] = [
    {
      id: 'technic',
      condition: (row: ITechCheck) => true,
      content: (row: ITechCheck) => (
        <LabeledCell
          title="Техник"
          value={
            fullName({
              lastName: row.technicLastName,
              firstName: row.technicFirstName,
              middleName: row.technicMiddleName,
            }) || 'Не указан'
          }
          colSpan={6}
        />
      ),
    },
  ];

  // Конфигурация расширяемых строк
  const expandedRows: ExpandedRowConfig[] = [
    {
      id: 'details',
      content: (row: ITechCheck) => (
        <>
          <LabeledCell title="Двигатель" value={row.engine ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Оптика" value={row.optic ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Кузов и салон" value={row.body ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Ходовая" value={row.chassis ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Одометр" value={row.odometerReading ? row.odometerReading + ' км' : '-'} />
          <LabeledCell
            title="Заключение"
            value={
              row.checkStatus === 'completed' ? 'Допущен' : row.checkStatus === 'rejected' ? 'Не допущен' : 'Ожидает'
            }
          />
        </>
      ),
    },
    {
      id: 'photos',
      content: (row: ITechCheck) => {
        const hasPhotos =
          row.odometerPhoto || (row.carImages && Array.isArray(row.carImages) && row.carImages.length > 0);
        if (!hasPhotos) return null;

        return (
          <td colSpan={6}>
            <div className="photoSection">
              {row.odometerPhoto && (
                <div className="photoContainer">
                  <div className="photoTitle">Фото одометра:</div>
                  <img src={normalizeImageUrl(row.odometerPhoto)} alt="Фото одометра" className="odometerPhoto" />
                </div>
              )}

              {row.carImages && Array.isArray(row.carImages) && (
                <SimpleManyPhotoContainer
                  carPhotos={{
                    frontPhoto: row.carImages.find((img: ICarImage) => img?.photoType === 'front')?.photoUrl || null,
                    backPhoto: row.carImages.find((img: ICarImage) => img?.photoType === 'back')?.photoUrl || null,
                    leftPhoto: row.carImages.find((img: ICarImage) => img?.photoType === 'left')?.photoUrl || null,
                    rightPhoto: row.carImages.find((img: ICarImage) => img?.photoType === 'right')?.photoUrl || null,
                    trunkPhoto: row.carImages.find((img: ICarImage) => img?.photoType === 'trunk')?.photoUrl || null,
                  }}
                  title="Фотографии автомобиля"
                />
              )}
            </div>
          </td>
        );
      },
    },
    {
      id: 'refusal',
      content: (row: ITechCheck) =>
        row.reasonForRefusal ? (
          <td colSpan={6}>
            <div className="reasonSection">
              <div className="reasonTitle">Причина отказа:</div>
              <div className="reasonText">{row.reasonForRefusal}</div>
            </div>
          </td>
        ) : null,
    },
  ];

  // Подсчет статистики технических осмотров
  const techStats = useMemo(() => {
    const totalCount = techChecks?.length || 0;
    const positiveCount = techChecks?.filter((check) => check.checkStatus === 'completed').length || 0;
    const negativeCount = techChecks?.filter((check) => check.checkStatus === 'rejected').length || 0;

    return {
      totalCount,
      positiveCount,
      negativeCount,
    };
  }, [techChecks]);

  // Загрузка данных с учетом фильтров
  useEffect(() => {
    async function fetchTechChecks() {
      if (!driver?.id) return;

      setLoading(true);
      setError('');

      try {
        // Приводим фильтры к нужному типу TechCheckFilters перед передачей
        const apiFilters: TechCheckFilters = {
          page: filters.page,
          limit: filters.limit,
          startTime: filters.startTime,
          endTime: filters.endTime,
        };

        const response = await TechCheckService.getDriverTechChecks(driver.id, apiFilters);

        // Преобразуем полученные данные к типу ITechCheck[]
        const formattedChecks: ITechCheck[] = (response.checks || []).map((check: any) => ({
          ...check,
          num: check.num.toString(), // Преобразуем num в строку, если нужно
        }));

        setTechChecks(formattedChecks);

        // Вычисляем количество страниц
        const totalPages = Math.ceil((response.total || 0) / (filters.limit || 10));
        setPagination({
          currentPage: filters.page || 1,
          totalPages: totalPages || 1,
          totalCount: response.total || 0,
        });
      } catch (error) {
        console.error('Ошибка при загрузке технических осмотров:', error);
        setError('Не удалось загрузить данные о технических осмотрах');

        // Сбрасываем данные при ошибке
        setTechChecks([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchTechChecks();
  }, [driver, filters]);

  if (loading) return <Spiner />;

  if (error) return <div className="errorMessage">{error}</div>;

  return (
    <div className="driverTechCheckList">
      <div className="infoCard">
        {/* Блок фильтров */}
        <DateRangeFilter
          startTime={filters.startTime}
          endTime={filters.endTime}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          className="techFilters"
        />

        {/* Блок статистики технических осмотров */}
        <div className="techStatsContainer" style={{ padding: '10px 25px' }}>
          <div className="techStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
            Осмотров: {techStats.totalCount}, из них Положительных {techStats.positiveCount}, Отрицательных{' '}
            {techStats.negativeCount}
          </div>
        </div>

        {loading ? (
          <div className="loadingIndicator">Загрузка...</div>
        ) : error ? (
          <div className="errorMessage">{error}</div>
        ) : (
          <>
            <div className="admListContainer">
              <Table
                data={techChecks}
                columns={tableColumns}
                expandedRows={expandedRows}
                secondaryRows={secondaryRows}
                keyField="id"
                noDataMessage="Нет данных о технических осмотрах"
                // className="techcheck-table"
              />
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
